/**
 * G-168 (owner ruling 2026-09-29): the pricing percentage is a MARGIN on the
 * price before IVA, not a markup on cost, and every screen that shows the
 * percentage shows its multiplier beside it so nobody reads 60 % as "×1.6".
 *
 *   price before IVA = cost ÷ (1 − margin)      → 60 % is ×2.5
 *   price with IVA   = that × (1 + IVA rate)    → ×2.75 at 10 %
 *
 * Display only: the price itself is worked out on the server
 * (`src/common/pricing/price-floor.ts`); this file never produces a price.
 */

/** "×2.5" for 60; null when the draft is not a margin under 100. */
export function marginMultiplier(targetMarginPercent: number): number | null {
  if (
    !Number.isFinite(targetMarginPercent) ||
    targetMarginPercent < 0 ||
    targetMarginPercent >= 100
  ) {
    return null;
  }
  return 1 / (1 - targetMarginPercent / 100);
}

function multiplierText(n: number): string {
  // Two decimals, trailing zeros dropped: 2.5, 2.75, 1.43.
  return `×${Number(n.toFixed(2))}`;
}

/** A percent box's draft as a number; NaN while it is empty or not a number. */
function parsePercentDraft(draft: string | number | null | undefined): number {
  if (draft == null) return Number.NaN;
  if (typeof draft === "number") return draft;
  const trimmed = draft.trim();
  return trimmed === "" ? Number.NaN : Number(trimmed);
}

/**
 * The label beside a target-margin box: "×2.5 before IVA · ×2.75 with IVA".
 * Without a usable IVA rate only the first part; null while the margin draft
 * is not a number under 100 (the box is empty or being typed).
 */
export function marginMultiplierLabel(
  targetMarginPercent: string | number,
  vatRatePercent?: string | number | null,
): string | null {
  const net = marginMultiplier(parsePercentDraft(targetMarginPercent));
  if (net === null) return null;
  const vat = parsePercentDraft(vatRatePercent);
  const before = `${multiplierText(net)} before IVA`;
  if (!Number.isFinite(vat) || vat < 0 || vat > 100) return before;
  return `${before} · ${multiplierText(net * (1 + vat / 100))} with IVA`;
}
